/**
 * 草图几何关系类型常量。
 *
 * 有限状态使用 `as const + 联合类型`，避免 TypeScript enum 生成额外运行时代码。
 * 当前领域只保留唯一的共圆心同半径标识 `Coradial`；历史 `Congruent` 在持久化解析边界迁移。
 */
export const SketchRelationKind = {
    /** 水平。 */
    Horizontal: 'Horizontal',
    /** 竖直。 */
    Vertical: 'Vertical',
    /** 共线。 */
    Collinear: 'Collinear',
    /** 共圆心且同半径。 */
    Coradial: 'Coradial',
    /** 垂直。 */
    Perpendicular: 'Perpendicular',
    /** 平行。 */
    Parallel: 'Parallel',
    /** 与 YZ 平行。 */
    ParallelToYZ: 'ParallelToYZ',
    /** 与 ZX 平行。 */
    ParallelToZX: 'ParallelToZX',
    /** 沿 Z。 */
    AlongZ: 'AlongZ',
    /** 相切。 */
    Tangent: 'Tangent',
    /** 同心。 */
    Concentric: 'Concentric',
    /** 中点。 */
    Midpoint: 'Midpoint',
    /** 交点。 */
    Intersection: 'Intersection',
    /** 重合。 */
    Coincident: 'Coincident',
    /** 距离。 */
    Distance: 'Distance',
    /** 相等。 */
    Equal: 'Equal',
    /** 相等曲率。 */
    EqualCurvature: 'EqualCurvature',
    /** 曲线长度相等。 */
    EqualCurveLength: 'EqualCurveLength',
    /** 对称。 */
    Symmetric: 'Symmetric',
    /** 固定。 */
    Fix: 'Fix',
    /** 角度。 */
    Angle: 'Angle',
    /** 三点定角。 */
    ThreePointAngle: 'ThreePointAngle',
    /** 半径。 */
    Radius: 'Radius',
    /** 直径。 */
    Diameter: 'Diameter',
    /** 固定槽口。 */
    FixSlot: 'FixSlot',
    /** 穿透。 */
    Pierce: 'Pierce',
    /** 合并点。 */
    MergePoints: 'MergePoints',
    /** 两倍距离。 */
    TwiceDistance: 'TwiceDistance',
    /** 相等槽口。 */
    EqualSlot: 'EqualSlot',
    /** 由草图阵列定义驱动的派生实体。 */
    Patterned: 'Patterned',
    /** 在线上。 */
    OnEdge: 'OnEdge',
    /** 在平面上。 */
    OnPlane: 'OnPlane',
    /** 在曲面上。 */
    OnSurface: 'OnSurface',
    /** 与面相切。 */
    TangentToFace: 'TangentToFace',
    /** 扭转连续性。 */
    TorsionContinuity: 'TorsionContinuity',
} as const;

/** 草图几何关系类型。 */
export type SketchRelationKind = typeof SketchRelationKind[keyof typeof SketchRelationKind];

/** 草图几何关系分类常量。 */
export const SketchRelationCategory = {
    /** 仅作用于单个实体自身。 */
    Self: 'self',
    /** 由多个实体共同形成关系。 */
    Multi: 'multi',
} as const;

/** 草图几何关系分类。 */
export type SketchRelationCategory = typeof SketchRelationCategory[keyof typeof SketchRelationCategory];

/** 只作用于单个实体自身的关系集合。 */
const SELF_RELATION_KIND_SET: ReadonlySet<SketchRelationKind> = new Set<SketchRelationKind>([
    SketchRelationKind.Horizontal,
    SketchRelationKind.Vertical,
    SketchRelationKind.Fix,
    SketchRelationKind.Radius,
    SketchRelationKind.Diameter,
    SketchRelationKind.FixSlot,
]);

/**
 * 当前生产代码可创建的关系类型集合。
 *
 * 集合覆盖手工关系、尺寸关系视图和槽口专属关系；外部引用的 `OnEdge` 由引用领域服务创建，
 * 不属于通用关系创建服务。其余已声明但尚无公式/构造器闭环的关系必须保持不可用。
 */
const CREATABLE_SKETCH_RELATION_KIND_SET: ReadonlySet<SketchRelationKind> = new Set<SketchRelationKind>([
    SketchRelationKind.Horizontal,
    SketchRelationKind.Vertical,
    SketchRelationKind.Collinear,
    SketchRelationKind.Coradial,
    SketchRelationKind.Perpendicular,
    SketchRelationKind.Parallel,
    SketchRelationKind.Tangent,
    SketchRelationKind.Concentric,
    SketchRelationKind.Midpoint,
    SketchRelationKind.Intersection,
    SketchRelationKind.Coincident,
    SketchRelationKind.Distance,
    SketchRelationKind.Equal,
    SketchRelationKind.EqualCurveLength,
    SketchRelationKind.Symmetric,
    SketchRelationKind.Fix,
    SketchRelationKind.Angle,
    SketchRelationKind.ThreePointAngle,
    SketchRelationKind.Radius,
    SketchRelationKind.Diameter,
    SketchRelationKind.FixSlot,
    SketchRelationKind.EqualSlot,
]);

/**
 * 只允许由具体领域服务创建的内部关系集合。
 *
 * 这些关系具备参数公式、历史和持久化闭环，但不能通过通用“添加几何关系”
 * 面板手工创建。
 */
const DOMAIN_MANAGED_SKETCH_RELATION_KIND_SET: ReadonlySet<SketchRelationKind> = new Set<SketchRelationKind>([
    SketchRelationKind.Patterned,
]);

/**
 * 解析关系分类。
 *
 * @param kind 关系类型。
 * @returns 关系分类。
 */
export function getSketchRelationCategory(kind: SketchRelationKind): SketchRelationCategory {
    return SELF_RELATION_KIND_SET.has(kind)
        ? SketchRelationCategory.Self
        : SketchRelationCategory.Multi;
}

/**
 * 判断关系是否允许向已有关系追加实体。
 *
 * @param kind 关系类型。
 * @returns 多目标关系返回 `true`。
 */
export function allowAppendSketchRelationTarget(kind: SketchRelationKind): boolean {
    return getSketchRelationCategory(kind) === SketchRelationCategory.Multi;
}

/**
 * 判断关系类型是否已具备生产创建闭环。
 *
 * @param kind 关系类型。
 * @returns 通用关系创建或尺寸关系视图能够创建该类型时返回 `true`。
 */
export function isCreatableSketchRelationKind(kind: SketchRelationKind): boolean {
    return CREATABLE_SKETCH_RELATION_KIND_SET.has(kind);
}

/**
 * 获取可创建关系类型快照。
 *
 * @returns 按类型字符串排序的只读数组。
 */
export function getCreatableSketchRelationKinds(): readonly SketchRelationKind[] {
    return [...CREATABLE_SKETCH_RELATION_KIND_SET].sort((left, right) => left.localeCompare(right));
}

/**
 * 获取领域托管关系类型快照。
 *
 * @returns 按类型字符串排序的只读数组。
 */
export function getDomainManagedSketchRelationKinds(): readonly SketchRelationKind[] {
    return [...DOMAIN_MANAGED_SKETCH_RELATION_KIND_SET].sort((left, right) => left.localeCompare(right));
}
